import React, { useMemo, useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const SharedOverview = ({ 
  session, 
  segments, 
  events, 
  company, 
  project, 
  projectConfig,
  shareInfo,
  downloads 
}) => {
  // Calculate stats
  const stats = useMemo(() => {
    const validSegments = segments?.filter(s => !s.exception && s.iri_avg_in_per_mi > 0) || [];
    const exceptionSegments = segments?.filter(s => s.exception) || [];
    const threshold = projectConfig?.iri_threshold_type_a || 160;

    if (!validSegments.length) {
      return {
        totalDistanceFt: 0,
        totalDistanceMi: 0,
        segmentCount: 0,
        exceptionCount: exceptionSegments.length,
        avgMRI: 0,
        maxMRI: 0,
        minMRI: 0,
        threshold,
      };
    }

    const mris = validSegments.map(s => s.iri_avg_in_per_mi);
    const totalDistanceFt = validSegments.reduce((sum, s) => sum + (s.distance_ft || 0), 0);

    return {
      totalDistanceFt,
      totalDistanceMi: totalDistanceFt / 5280,
      segmentCount: validSegments.length,
      exceptionCount: exceptionSegments.length,
      avgMRI: mris.reduce((a, b) => a + b, 0) / mris.length,
      maxMRI: Math.max(...mris),
      minMRI: Math.min(...mris),
      threshold,
    };
  }, [segments, projectConfig]);

  // Event counts
  const eventCounts = useMemo(() => {
    const threshold = projectConfig?.iri_threshold_type_a || 160;
    const alrEvents = events?.filter(e => e.event_type === 'LR' && e.magnitude_in > threshold) || [];
    const bumpEvents = events?.filter(e => e.event_type === 'Bump') || [];
    const dipEvents = events?.filter(e => e.event_type === 'Dip') || [];

    return {
      alr: alrEvents.length,
      bump: bumpEvents.length,
      dip: dipEvents.length,
    };
  }, [events, projectConfig]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format station from feet to station+offset format (e.g., 40244160 → 402441+60)
  const formatStation = (ft) => {
    if (!ft) return '—';
    const station = Math.floor(ft / 100);
    const offset = Math.round(ft % 100);
    return `${station}+${offset.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 sm:py-6">
      {/* Main Card */}
      <div className="bg-white sm:border border-gray-200 sm:rounded-sm">
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200">
          {/* Mobile: Stack vertically, Desktop: Side by side */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            {/* Left - Session Info */}
            <div className="min-w-0 flex-1">
              {company?.name && (
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  {company.name}
                </p>
              )}
              <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {session?.name || session?.filename || 'Session Report'}
              </h1>
              {project?.name && (
                <p className="text-sm text-gray-500 mt-0.5 truncate">{project.name}</p>
              )}
            </div>

            {/* Right - Share Info & Download */}
            <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 sm:gap-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100">
              <div className="text-xs text-gray-500 sm:text-right">
                {(shareInfo?.contact_name || shareInfo?.contact_email) && (
                  <p>
                    Shared by{' '}
                    {shareInfo.contact_email ? (
                      <a href={`mailto:${shareInfo.contact_email}`} className="text-red-600 hover:underline">
                        {shareInfo.contact_name || shareInfo.contact_email}
                      </a>
                    ) : (
                      shareInfo.contact_name
                    )}
                  </p>
                )}
                <p className="hidden sm:block mt-0.5">
                  {shareInfo?.expires_at 
                    ? `Expires ${formatDate(shareInfo.expires_at)}`
                    : 'No expiration'
                  }
                </p>
              </div>
              
              {/* Download Button */}
              {downloads && downloads.length > 0 && (
                <DownloadDropdown downloads={downloads} />
              )}
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-200 border-b border-gray-200">
          <MetricCell label="Run Date" value={formatDate(session?.run_date)} />
          <MetricCell label="Distance" value={`${stats.totalDistanceMi.toFixed(2)} mi`} />
          <MetricCell label="Segments" value={stats.segmentCount} />
          <MetricCell label="Roughness" value={eventCounts.alr} sublabel="ALR" />
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            {/* Left Column */}
            <div className="space-y-3 sm:space-y-4">
              {/* Session Details */}
              <Section title="Session Details">
                <DataRow label="Direction" value={session?.direction || '—'} />
                <DataRow label="Lane" value={session?.lane_no || '—'} />
                <DataRow label="Profiler" value={session?.profiler_model || session?.meta?.model_num || '—'} />
                <DataRow label="Serial #" value={session?.meta?.serial_num || '—'} />
                <DataRow label="Operator" value={session?.operator || '—'} />
              </Section>

              {/* Coverage */}
              <Section title="Coverage">
                <DataRow label="Total Distance" value={`${stats.totalDistanceMi.toFixed(3)} mi`} />
                <DataRow label="Start Station" value={formatStation(session?.start_station_ft)} />
                <DataRow label="End Station" value={formatStation(session?.end_station_ft)} />
              </Section>
            </div>

            {/* Right Column */}
            <div className="space-y-3 sm:space-y-4">
              {/* Fixed Interval Statistics */}
              <Section title="Fixed Interval Statistics">
                <DataRow label="Segment Length" value={session?.meta?.['reduction_length(ft.)'] ? `${session.meta['reduction_length(ft.)']} ft` : '—'} />
                <DataRow label="Average MRI" value={`${stats.avgMRI.toFixed(1)} in/mi`} />
                <DataRow label="Maximum MRI" value={`${stats.maxMRI.toFixed(1)} in/mi`} />
                <DataRow label="Minimum MRI" value={`${stats.minMRI.toFixed(1)} in/mi`} />
                <DataRow label="Threshold" value={`${stats.threshold} in/mi`} />
              </Section>

              {/* Events */}
              <Section title="Detected Events">
                <DataRow label="Localized Roughness" value={eventCounts.alr} />
                <DataRow label="Bumps" value={eventCounts.bump} />
                <DataRow label="Dips" value={eventCounts.dip} />
              </Section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Section within the card - with border
const Section = ({ title, children }) => (
  <div className="border border-gray-200 rounded-sm overflow-hidden">
    <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</h3>
    </div>
    <div className="px-3 py-2 space-y-1.5">
      {children}
    </div>
  </div>
);

// Metric cell for the stats bar
const MetricCell = ({ label, value, unit, sublabel }) => (
  <div className="px-2 sm:px-4 py-3 text-center">
    <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 truncate">{label}</p>
    <p className="text-base sm:text-lg font-semibold text-gray-900">
      {value}
      {unit && <span className="text-xs font-normal text-gray-400 ml-0.5">{unit}</span>}
      {sublabel && <span className="text-[10px] sm:text-xs font-normal text-gray-400 ml-1">{sublabel}</span>}
    </p>
  </div>
);

// Data row for content
const DataRow = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm gap-2">
    <span className="text-gray-500 truncate flex-shrink-0">{label}</span>
    <span className="font-medium text-gray-900 truncate text-right">{value}</span>
  </div>
);

// Download dropdown component
const DownloadDropdown = ({ downloads }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getFileLabel = (file) => {
    if (file.file_type === 'raw_profile_data') return 'PPF';
    if (file.file_type === 'proval_project') return 'PV2';
    return file.filename.split('.').pop()?.toUpperCase() || 'File';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const handleDownload = (file) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsOpen(false);
  };

  return (
    <div className="relative sm:mt-2" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 active:bg-gray-100 transition-colors"
      >
        <ArrowDownTrayIcon className="h-4 w-4" />
        <span className="hidden xs:inline">Download</span>
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-64 sm:w-56 bg-white border border-gray-200 rounded-sm shadow-lg z-20">
          <div className="py-1">
            {downloads.map((file) => (
              <button
                key={file.id}
                onClick={() => handleDownload(file)}
                className="w-full px-3 py-2.5 sm:py-2 text-left text-sm hover:bg-gray-50 active:bg-gray-100 flex items-center justify-between group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-flex items-center justify-center w-8 h-5 text-xs font-medium bg-gray-100 text-gray-600 rounded flex-shrink-0">
                    {getFileLabel(file)}
                  </span>
                  <span className="text-gray-700 truncate">
                    {getFileLabel(file) === 'PPF' ? 'Raw Profile Data' : 'ProVAL Project File'}
                  </span>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                  {formatFileSize(file.file_size)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedOverview;